import { createContext, useContext, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Zap } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import KrispButton from '../components/KrispButton';

const WaitlistContext = createContext(null);

export const useWaitlist = () => useContext(WaitlistContext);

export function WaitlistProvider({ children }) {
  const [interest, setInterest] = useState(null);
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const openWaitlist = (item = 'THE FULL DROP') => { setInterest(item); setDone(false); setEmail(''); };
  const close = () => setInterest(null);

  const join = async (e) => {
    e.preventDefault();
    if (!email.includes('@')) return;
    setBusy(true);
    try {
      await api.post('/waitlist', { email, interest });
      setDone(true);
      toast("YOU'RE ON THE LIST ✓");
    } catch {
      toast('THAT FUMBLED. TRY AGAIN.');
    } finally { setBusy(false); }
  };

  return (
    <WaitlistContext.Provider value={{ openWaitlist }}>
      {children}
      <AnimatePresence>
        {interest && (
          <>
            <motion.div
              data-testid="waitlist-overlay"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={close}
              className="fixed inset-0 z-[400] bg-black/75 backdrop-blur-sm"
            />
            <motion.div
              data-testid="waitlist-modal"
              initial={{ opacity: 0, y: 60, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.96 }}
              transition={{ type: 'spring', stiffness: 260, damping: 24 }}
              className="fixed z-[410] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-md bg-cream border-2 border-cocoa rounded-[2rem] p-8 text-cocoa shadow-[10px_10px_0_#FFC700]"
            >
              <button data-testid="waitlist-close" data-cursor="plus" onClick={close} aria-label="Close waitlist" className="absolute top-4 right-4 w-9 h-9 rounded-full border-2 border-cocoa flex items-center justify-center hover:bg-kyellow transition-colors duration-200">
                <X size={16} />
              </button>
              <p className="text-[10px] tracking-[0.4em] text-maroon font-black flex items-center gap-2"><Zap size={11} /> COMING SOON</p>
              <h3 className="font-display text-5xl mt-3 leading-none">THE DROP ISN'T<br />LIVE <span className="text-kred">YET.</span></h3>
              <p className="text-sm text-cocoa/70 mt-3 leading-relaxed">Join the waitlist. First crunch goes to the list — early access, drop alerts, secret codes.</p>
              <div className="mt-5 rounded-2xl border-2 border-cocoa/15 bg-white/70 px-4 py-2.5">
                <p className="text-[9px] tracking-[0.3em] text-cocoa/50 font-bold">YOU'RE WAITING FOR</p>
                <p className="font-marker text-kred text-base mt-0.5" data-testid="waitlist-interest">{interest}</p>
              </div>
              {done ? (
                <div className="mt-6 rounded-2xl border-2 border-cocoa bg-kyellow p-5 text-center" data-testid="waitlist-success">
                  <p className="font-display text-3xl text-cocoa">YOU'RE LOCKED IN ✓</p>
                  <p className="text-xs text-cocoa/70 mt-1">Watch your inbox. The crunch is coming.</p>
                </div>
              ) : (
                <form onSubmit={join} className="mt-6 space-y-3">
                  <input
                    data-testid="waitlist-email"
                    type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email" className="k-input"
                  />
                  <KrispButton testid="waitlist-submit" type="submit" disabled={busy} className="w-full">
                    {busy ? 'LOCKING...' : 'LOCK MY SPOT →'}
                  </KrispButton>
                </form>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </WaitlistContext.Provider>
  );
}
